import { useState, FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { validateEmail } from '../utils/validation';
import { isValidPhone, normalizePhone } from '../utils/phone';

interface AddEmployeeFormProps {
  onAdded: () => void;
}

export const AddEmployeeForm = ({ onAdded }: AddEmployeeFormProps) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError('Enter a valid email');
      return;
    }
    if (phone && !isValidPhone(phone)) {
      setError('Enter a valid mobile number');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const adminSession = sessionData.session;

      const normalizedPhone = phone ? normalizePhone(phone) : null;

      const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) throw signUpError;
      if (!data.user) throw new Error('User creation failed');

      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        email,
        phone: normalizedPhone,
        full_name: fullName,
        role: 'EMPLOYEE',
        department: department || null,
        status: 'ACTIVE',
      });
      if (profileError) throw profileError;

      // Restore admin session if signUp automatically signed in the new employee
      if (data.session && adminSession) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token,
        });
        if (sessionError) throw sessionError;
      }

      setEmail('');
      setPhone('');
      setPassword('');
      setFullName('');
      setDepartment('');
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';

  return (
    <form onSubmit={handleSubmit} className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
          required
        />
        <input
          type="tel"
          placeholder="Mobile number (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputClass}
        />
        <input
          type="password"
          placeholder="Password (min 6)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
          required
          minLength={6}
        />
        <input
          type="text"
          placeholder="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className={inputClass}
          required
        />
        <input
          type="text"
          placeholder="Department (optional)"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className={inputClass}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="mt-4 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? 'Creating...' : 'Add Employee'}
      </button>
    </form>
  );
};
